<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import { useUserStore } from "@/features/users/user.store";
import { useShiftStore } from "@/features/shifts/shifts.store";
import { useTeamStore } from "@/features/teams/team.store";
import { useNotification } from "@/utils/notification";
import { IUser } from "@/features/users/user.types";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { ITeam } from "@/features/teams/team.types";
import {
  Pencil02,
  Plus,
  SearchMd,
  Trash01,
  Users03,
  Lock01,
} from "utruckin-icons";
import router from "@/router";

const userStore = useUserStore();
const shiftStore = useShiftStore();
const teamStore = useTeamStore();
const { pushNotification } = useNotification();

const loading = ref(false);
const users = ref<IUser[]>([]);
const shifts = ref<IShiftTemplate[]>([]);
const teams = ref<ITeam[]>([]);

// ─── FILTERS ─────────────────────────────────────────────
const searchQuery = ref("");
const role = ref<string | null>(null);
const team = ref<string | null>(null);
const activeTab = ref<"users" | "graphic" | "team">("users");

let filterTimeout: ReturnType<typeof setTimeout> | null = null;

const roleItems = computed(() => {
  if (
    userStore.userProfile?.role == "admin" ||
    userStore.userProfile?.role == "super_admin"
  ) {
    return [
      { value: "worker", name: "Specialist" },
      { value: "supervisor", name: "Supervisor" },
      { value: "admin", name: "Admin" },
    ];
  } else {
    return [
      { value: "worker", name: "Specialist" },
      { value: "supervisor", name: "Supervisor" },
    ];
  }
});

const dayOffItems = [
  { value: "MON", name: "Monday" },
  { value: "TUE", name: "Tuesday" },
  { value: "WED", name: "Wednesday" },
  { value: "THU", name: "Thursday" },
  { value: "FRI", name: "Friday" },
  { value: "SAT", name: "Saturday" },
  { value: "SUN", name: "Sunday" },
];

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const shiftItems = computed(() =>
  shifts.value.map((s) => ({ value: String(s.id), name: s.title }))
);

// ─── USERS TAB ───────────────────────────────────────────
const getTeamName = (teamId: string | null): string => {
  if (!teamId) return "—";
  const found = teams.value.find((t) => t.id === teamId);
  return found?.name || "—";
};

const getRoleName = (role: string) => {
  if (!role) return "-";
  if (role == "admin") return "Admin";
  if (role == "supervisor") return "Supervisor";
  if (role == "worker") return "Specialist";
};

const tableGroups = computed(() => {
  const groups = shifts.value.map((shift) => ({
    label: shift.title,
    items: users.value
      .filter((user) => Number(user.shift_template_id) === Number(shift.id))
      .sort((a, b) => (a.team_name || "").localeCompare(b.team_name || "")),
  }));
  const withoutShift = users.value.filter((user) => !user.shift_template_id);
  if (withoutShift.length) {
    groups.push({ label: "Admin", items: withoutShift });
  }
  return groups;
});

const teamGroups = computed(() => {
  return shifts.value.map((shift) => {
    const shiftUsers = users.value.filter(
      (user) => Number(user.shift_template_id) === Number(shift.id)
    );

    const admins = shiftUsers.filter((user) => user.role === "supervisor");

    const items = teams.value
      .map((team) => ({
        label: team.name,
        items: shiftUsers.filter(
          (user) => user.role !== "supervisor" && user.team_id === team.id
        ),
      }))
      .filter((team) => team.items.length);

    return {
      label: shift.title,
      admins,
      items,
      count: shiftUsers.length,
    };
  });
});

const isAdmin = computed(() => {
  return (
    userStore.userProfile?.role == "admin" ||
    userStore.userProfile?.role == "super_admin" ||
    userStore.userProfile?.role == "supervisor"
  );
});

// ─── DELETE ──────────────────────────────────────────────
const deleteUser = async (id: string) => {
  await userStore.deactivateUser(loading, id);
  pushNotification("User deleted successfully", "success");
  users.value = await userStore.getUsers(loading);
};

// ─── ADD MODAL ───────────────────────────────────────────
const addModal = ref(false);
const addLoading = ref(false);
const addTouched = ref(false);

const addForm = ref({
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  phone_number: "",
  role: "",
  team_id: "",
  shift_template_id: "",
  monthly_wage: "",
  date_of_birth: "",
  working_since: "",
  default_day_off: "",
  is_active: true,
});

const addErrors = computed(() => ({
  first_name: addTouched.value && !addForm.value.first_name.trim(),
  last_name: addTouched.value && !addForm.value.last_name.trim(),
  email: addTouched.value && !addForm.value.email.trim(),
  password: addTouched.value && addForm.value.password.trim().length < 8,
}));

const passwordError = computed(() => {
  return addForm.value.password.trim()
    ? addForm.value.password.trim().length < 8
      ? "Password must be at least 8"
      : ""
    : "Password is required";
});

const openAddModal = () => {
  addTouched.value = false;
  addForm.value = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone_number: "",
    role: "",
    team_id: "",
    shift_template_id: "",
    monthly_wage: "",
    date_of_birth: "",
    working_since: "",
    default_day_off: "",
    is_active: true,
  };
  addModal.value = true;
};

const submitAdd = async () => {
  addTouched.value = true;
  if (
    !addForm.value.first_name.trim() ||
    !addForm.value.last_name.trim() ||
    !addForm.value.email.trim() ||
    !addForm.value.password.trim() ||
    addForm.value.password.trim().length < 8
  )
    return;

  const organizationId: string = localStorage.getItem(
    "organizationId"
  ) as string;
  if (!organizationId) router.push("/organization");

  const payload: any = {
    name: addForm.value.first_name + " " + addForm.value.last_name,
    email: addForm.value.email,
    password: addForm.value.password,
    phone_number: addForm.value.phone_number || null,
    role: addForm.value.role,
    is_active: addForm.value.is_active,
    monthly_wage: addForm.value.monthly_wage,
    date_of_birth: addForm.value.date_of_birth,
    working_since: addForm.value.working_since,
    default_day_off: addForm.value.default_day_off || null,
    ...(addForm.value.team_id ? { team_id: addForm.value.team_id } : {}),
    ...(addForm.value.shift_template_id
      ? { shift_template_id: Number(addForm.value.shift_template_id) }
      : {}),
    organization_id: organizationId,
  };
  const result = await userStore.createUser(addLoading, payload);
  if (result?.id) {
    pushNotification("User created successfully", "success");
    users.value = await userStore.getUsers(loading);
    addModal.value = false;
  }
};

// ─── EDIT MODAL ──────────────────────────────────────────
const editModal = ref(false);
const editLoading = ref(false);
const editTouched = ref(false);
const editUserId = ref<string>("");

const editForm = ref({
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  role: "",
  default_day_off: "",
  team_id: "",
  shift_template_id: "",
  is_active: true,
});

const editErrors = computed(() => ({
  first_name: editTouched.value && !editForm.value.first_name.trim(),
  last_name: editTouched.value && !editForm.value.last_name.trim(),
  email: editTouched.value && !editForm.value.email.trim(),
}));

const openEditModal = (item: IUser) => {
  editTouched.value = false;
  editUserId.value = item.id;
  editForm.value = {
    first_name: item.name.split(" ")[0],
    last_name: item.name.split(" ")[1],
    email: item.email,
    phone_number: item.phone_number || "",
    role: item.role,
    default_day_off: item.default_day_off || "",
    team_id: item.team_id || "",
    shift_template_id: item.shift_template_id
      ? String(item.shift_template_id)
      : "",
    is_active: item.is_active,
  };
  editModal.value = true;
};

const submitEdit = async () => {
  editTouched.value = true;
  if (
    !editForm.value.first_name.trim() ||
    !editForm.value.last_name.trim() ||
    !editForm.value.email.trim()
  )
    return;

  const payload: any = {
    name: editForm.value.first_name + " " + editForm.value.last_name,
    email: editForm.value.email,
    phone_number: editForm.value.phone_number || null,
    role: editForm.value.role,
    is_active: editForm.value.is_active,
    ...(editForm.value.default_day_off
      ? { default_day_off: editForm.value.default_day_off }
      : {}),
    ...(editForm.value.team_id ? { team_id: editForm.value.team_id } : {}),
    ...(editForm.value.shift_template_id
      ? { shift_template_id: Number(editForm.value.shift_template_id) }
      : {}),
  };

  const result = await userStore.updateUserProfile(
    editLoading,
    editUserId.value,
    payload
  );
  if (result?.id) {
    pushNotification("User updated successfully", "success");
    users.value = await userStore.getUsers(loading);
    editModal.value = false;
  }
};

// password modal

const password = ref<string>("");
const confirmPassword = ref<string>("");
const passwordModal = ref<boolean>(false);
const passwordLoading = ref<boolean>(false);
const passwordUserId = ref<string>("");

const openPasswordModal = (id: string) => {
  password.value = "";
  confirmPassword.value = "";
  passwordModal.value = true;
  passwordUserId.value = id;
};

const closePasswordModal = () => {
  password.value = "";
  confirmPassword.value = "";
  passwordModal.value = false;
  passwordUserId.value = "";
  searchQuery.value = "";
};

const savePasswordModal = async () => {
  if (!(password.value.length > 5) || password.value !== confirmPassword.value)
    return;

  const result = await userStore.changePassword(
    passwordLoading,
    passwordUserId.value,
    {
      new_password: password.value,
    }
  );

  if (result) {
    pushNotification("Password changed successfully", "success");
    closePasswordModal();
  }
};

onMounted(async () => {
  const shiftLoading = ref(false);
  const usersLoading = ref(false);
  const teamLoading = ref(false);

  [users.value, shifts.value, teams.value] = await Promise.all([
    userStore.getUsers(usersLoading),
    shiftStore.getShifts(shiftLoading),
    teamStore.getTeams(teamLoading),
  ]);

  if (userStore.commonFilter.q) {
    searchQuery.value = userStore.commonFilter.q;
  }

  if (userStore.commonFilter.role) {
    role.value = userStore.commonFilter.role;
  }

  if (userStore.commonFilter.team_id) {
    team.value = userStore.commonFilter.team_id;
  }
});

watch([searchQuery, role, team], () => {
  if (filterTimeout) clearTimeout(filterTimeout);
  filterTimeout = setTimeout(async () => {
    userStore.commonFilter.q = searchQuery.value;
    userStore.commonFilter.role = role.value ?? "";
    userStore.commonFilter.team_id = team.value ?? "";
    users.value = await userStore.getUsers(loading);
  }, 300);
});
</script>

<template>
  <div class="v-users">
    <div class="v-users__header">
      <div class="v-users__tabs">
        <button
          class="v-users__tab"
          :class="{ active: activeTab === 'users' }"
          @click="activeTab = 'users'"
        >
          Users
        </button>
        <button
          class="v-users__tab"
          :class="{ active: activeTab === 'team' }"
          @click="activeTab = 'team'"
        >
          Team
        </button>
      </div>
      <div class="v-users__header--right">
        <CommonSearch
          v-model="searchQuery"
          :icon="SearchMd"
          placeholder="Search"
          clearable
        />
        <CommonDropdown
          v-model="role"
          :items="roleItems"
          label="Role"
          clearable
        />
        <CommonDropdown
          v-model="team"
          :items="teamItems"
          label="Team"
          clearable
        />
        <CommonButton
          v-if="isAdmin"
          type="primary"
          label="Add User"
          :prepend="true"
          :prependIcon="Plus"
          @clicked="openAddModal"
        />
      </div>
    </div>

    <!-- USERS TAB -->
    <CommonTable
      v-if="activeTab === 'users'"
      :headers="userStore.userHeaders"
      :groups="tableGroups"
      :loading="loading"
    >
      <template #id="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template #title="{ item }">
        <span>{{ item.name }}</span>
      </template>

      <template #role="{ item }">
        <span class="v-users__badge">{{ getRoleName(item.role) }}</span>
      </template>

      <template #team="{ item }">
        <span>{{ getTeamName(item.team_id) }}</span>
      </template>

      <template #off_day="{ item }">
        <span>{{ item.default_day_off || "—" }}</span>
      </template>

      <template #actions="{ item }">
        <div v-if="isAdmin" class="v-users__actions">
          <button class="v-users__btn" @click="openEditModal(item)">
            <Pencil02 :size="18" />
          </button>
          <button class="v-users__btn" @click="deleteUser(item.id)">
            <Trash01 :size="18" />
          </button>
          <button
            v-if="isAdmin"
            class="v-users__btn"
            @click="openPasswordModal(item.id)"
          >
            <Lock01 :size="18" />
          </button>
        </div>
        <div v-else>-</div>
      </template>
    </CommonTable>

    <!--   Team tab   -->
    <div class="team" v-if="activeTab === 'team'">
      <div v-for="(item, index) in teamGroups" class="team__item" :key="index">
        <div class="team__item--shift">
          <div class="title">
            {{ item.label }}
          </div>

          <div class="info">
            <div class="icon">
              <Users03 :size="20" />
            </div>

            <span>{{ item.count }}</span>
          </div>
        </div>

        <div
          v-for="(admin, admIndex) in item.admins"
          :key="admIndex"
          class="team__item--admin"
        >
          <div class="name">{{ admin.name }}</div>
          <div class="role">{{ admin.role }}</div>
        </div>

        <div
          v-for="(team, teamIndex) in item.items"
          :key="teamIndex"
          class="team__group"
        >
          <div class="label">
            {{ team.label }}
          </div>

          <div
            v-for="(user, userIndex) in team.items"
            :key="userIndex"
            class="team__group--item"
          >
            <div class="name">{{ user.name }}</div>
            <div class="role">
              {{ user.role == "worker" ? "Specialist" : user.role }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ADD MODAL -->
    <CommonModal v-model="addModal" title="Add user">
      <div class="v-users__form">
        <div style="display: flex; justify-content: space-between">
          <CommonInput
            v-model="addForm.last_name"
            label="Last name"
            :error="addErrors.last_name"
            errorMessage="Last name is required"
            width="calc(50% - 8px)"
          />
          <CommonInput
            v-model="addForm.first_name"
            label="First name"
            :error="addErrors.first_name"
            errorMessage="First name is required"
            width="calc(50% - 8px)"
          />
        </div>

        <div style="display: flex; justify-content: space-between">
          <CommonInput
            v-model="addForm.email"
            label="Email"
            type="email"
            :error="addErrors.email"
            errorMessage="Email is required"
            autocomplete="email"
            width="calc(50% - 8px)"
          />
          <CommonInput
            v-model="addForm.password"
            label="Password"
            type="password"
            :error="addErrors.password"
            :errorMessage="passwordError"
            width="calc(50% - 8px)"
          />
        </div>

        <div style="display: flex; justify-content: space-between">
          <CommonInput
            v-model="addForm.monthly_wage"
            label="Monthly Wage"
            width="calc(50% - 8px)"
          />
          <CommonInput
            v-model="addForm.phone_number"
            label="Phone number"
            width="calc(50% - 8px)"
          />
        </div>

        <div style="display: flex; justify-content: space-between">
          <CommonInput
            mask="####-##-##"
            v-model="addForm.date_of_birth"
            label="Date of birth (YYYY-MM-DD)"
            width="calc(50% - 8px)"
          />
          <CommonInput
            mask="####-##-##"
            v-model="addForm.working_since"
            label="Working since (YYYY-MM-DD)"
            width="calc(50% - 8px)"
          />
        </div>

        <div style="display: flex; justify-content: space-between; gap: 16px">
          <CommonDropdown
            v-model="addForm.role"
            :items="roleItems"
            label="Role"
            :fullWidth="true"
          />
          <CommonDropdown
            v-model="addForm.team_id"
            :items="teamItems"
            label="Team"
            :fullWidth="true"
          />
        </div>
        <div style="display: flex; justify-content: space-between; gap: 16px">
          <CommonDropdown
            v-model="addForm.shift_template_id"
            :items="shiftItems"
            label="Shift"
            :fullWidth="true"
          />
          <CommonDropdown
            v-model="addForm.default_day_off"
            :items="dayOffItems"
            label="Day off"
            :fullWidth="true"
          />
        </div>
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="addModal = false"
          />
          <CommonButton
            type="primary"
            label="Save"
            :loading="addLoading"
            @clicked="submitAdd"
          />
        </div>
      </template>
    </CommonModal>

    <!-- EDIT MODAL -->
    <CommonModal v-model="editModal" title="Edit user">
      <div class="v-users__form">
        <CommonInput
          v-model="editForm.last_name"
          label="Last name"
          :error="editErrors.last_name"
          errorMessage="Last name is required"
        />
        <CommonInput
          v-model="editForm.first_name"
          label="First name"
          :error="editErrors.first_name"
          errorMessage="First name is required"
        />
        <CommonInput
          v-model="editForm.email"
          label="Email"
          type="email"
          :error="editErrors.email"
          errorMessage="Email is required"
          :disabled="userStore.userProfile?.role !== 'super_admin'"
        />
        <CommonInput v-model="editForm.phone_number" label="Phone number" />
        <CommonDropdown
          v-model="editForm.role"
          :items="roleItems"
          label="Role"
          :fullWidth="true"
        />
        <CommonDropdown
          v-model="editForm.team_id"
          :items="teamItems"
          label="Team"
          :fullWidth="true"
        />
        <CommonDropdown
          v-model="editForm.shift_template_id"
          :items="shiftItems"
          label="Shift"
          :fullWidth="true"
        />
        <CommonDropdown
          v-model="editForm.default_day_off"
          :items="dayOffItems"
          label="Day off"
          :fullWidth="true"
        />
      </div>

      <template #footer>
        <div
          style="
            display: flex;
            justify-content: flex-end;
            width: 100%;
            gap: 16px;
          "
        >
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="editModal = false"
          />
          <CommonButton
            type="primary"
            label="Save"
            :loading="editLoading"
            @clicked="submitEdit"
          />
        </div>
      </template>
    </CommonModal>

    <!--  Password Modal  -->
    <CommonModal v-model="passwordModal" title="Password change">
      <div class="new-modal">
        <CommonInput type="password" v-model="password" label="New password" />
        <CommonInput
          type="password"
          v-model="confirmPassword"
          label="Confirm password"
        />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton
            type="outline"
            label="Cancel"
            @clicked="closePasswordModal"
          />
          <CommonButton
            :is-disabled="
              !(password.length > 5) || password !== confirmPassword
            "
            type="primary"
            label="Save"
            @clicked="savePasswordModal"
          />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-users {
  padding: var(--base-space-24);
  display: flex;
  flex-direction: column;
  gap: var(--base-space-24);
  background: var(--color-system-background);
  height: calc(100vh - 80px);

  &__tabs {
    height: 44px;
    display: flex;
    align-items: center;
    background: var(--color-tertiary-system-background);
    border-radius: var(--base-radius-14);
    padding: 4px;
    gap: var(--base-space-16);
  }

  &__tab {
    padding: 6px 16px;
    border-radius: var(--base-radius-12);
    border: none;
    background: transparent;
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;
    color: var(--color-label);
    cursor: pointer;
    transition: all 0.15s ease;

    &.active {
      background: var(--color-system-background);
      font-weight: 500;
      box-shadow: 0 2px 20px 0 rgba(0, 0, 0, 0.1);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &--right {
      display: flex;
      align-items: center;
      gap: var(--base-space-8);
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: var(--base-space-12);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    border-radius: var(--base-radius-8);
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-transform: capitalize;
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    border: 1px solid var(--color-border);
    transition: opacity 0.2s;
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }

  .team {
    display: flex;
    justify-content: space-between;
    overflow: auto;
    gap: 12px;
    color: var(--color-label);
    font-size: 16px;
    line-height: 24px;
    font-weight: 500;

    &__item {
      flex: 1;

      &--shift {
        box-shadow: 0 2px 20px 0 #a6a6a640;
        padding: 12px 16px;
        border-radius: 12px;
        background: var(--color-system-background);
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 8px 0;

        .info {
          display: flex;
          align-items: center;
          gap: 4px;

          .icon {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }

      &--admin {
        background: var(--color-system-background);
        border: 1px solid var(--color-border);
        border-radius: 12px;
        display: flex;
        justify-content: space-between;
        padding: 10px 12px;
        margin-bottom: 8px;

        .role {
          background: var(--color-green-light);
          border-radius: 12px;
          padding: 2px 8px;
          text-transform: capitalize;
        }
      }

      .team__group {
        border: 1px solid var(--color-border);
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        margin-bottom: 8px;

        .label {
          border-bottom: 1px solid var(--color-border);
          padding: 12px;
          background: var(--color-secondary-system-background);
        }

        &--item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;

          &:not(:last-child) {
            border-bottom: 1px solid var(--color-border);
          }

          .role {
            background: var(--color-grey-light);
            border-radius: 12px;
            padding: 2px 8px;
            text-transform: capitalize;
          }
        }
      }
    }
  }
}

.new-modal {
  display: flex;
  flex-direction: column;
  gap: var(--base-space-16);
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}
</style>
